import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../framework/redux/hooks";
import { MainLayout } from "../layout/mainLayout";
import { PageTitle } from "../layout/pageTitle";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import { Base } from "../../framework/common/base";
import { useNavigate } from "react-router-dom";
import { fetchNotifications, setLastUnconfirmedCount } from "../../framework/redux/slices/notificationSlice";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { NotificationsPageTitle } from "../layout/notificationsPageTitle";
import { transportOrderStateNames } from "../../models/transport/transportOrder";
import { useSelector } from "react-redux";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { optionOwnerHasTransportNotificationsEnabled } from "../../models/common/consts";

export const Notifications = () => {
    const [notificationsEnabled, optionLoading] = useAppOptionValueEquals(
        optionOwnerHasTransportNotificationsEnabled,
        true
    );
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const employeeId = useAppSelector((state) => state.session.id);
    const user = useAppSelector(({ session }) => session.fullName);
    const notifications = useAppSelector((state) => state.notification.notifications);
    const loading = useAppSelector((state) => state.notification.loading);
    const lastUnconfirmedCount = useSelector(
        (state: any) => state.notification.lastUnconfirmedCount
    );

    useEffect(() => {
        if (!(optionLoading || notificationsEnabled)) {
            navigate("/", { replace: true });
        }
    }, [optionLoading, notificationsEnabled]);

    const groupedNotifications = notifications.reduce((acc, notification) => {
        const { transportOrderId } = notification;
        if (!acc[transportOrderId]) {
            acc[transportOrderId] = [];
        }
        acc[transportOrderId].push(notification);
        return acc;
    }, {} as Record<string, typeof notifications>);

    useEffect(() => {
        void dispatch(fetchNotifications(employeeId));
    }, [dispatch]);

    useEffect(() => {
        if (loading) return;

        // Hack to update lastUnconfirmedCount
        if (lastUnconfirmedCount > Object.keys(groupedNotifications).length) {
            void dispatch(setLastUnconfirmedCount(Object.keys(groupedNotifications).length));
        }

    }, [groupedNotifications]);

    if (loading) {
        return (
            <MainLayout
                topComponent={<PageTitle header={t("notifications.notifications")} />}
                hasPx
            >
                <Typography>{t("common.loading")}</Typography>
            </MainLayout>
        );
    }

    return (
        <MainLayout
            topComponent={
                    notifications.length > 0 ? (
                        <NotificationsPageTitle header={t("notifications.greeting")} text={user} />
                    ) : (
                        <PageTitle header={t("notifications.notifications")} />
                    )
                }
            hasPx
        >
            {Object.keys(groupedNotifications).length > 0 ? (
                Object.entries(groupedNotifications).map(([orderId, orderNotifications]) => (
                    <Box key={orderId} sx={{ mb: 4 }}>
                        <Grid2 container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                            <Typography variant="h6" fontWeight="bold">
                                {t("notifications.order")} {orderNotifications[0].transportOrderNumber}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/transport/order/${orderId}`)}
                                sx={{
                                    backgroundColor: "#1976d2",
                                    textTransform: "none",
                                }}
                            >
                                {t("notifications.viewDetails")}
                            </Button>
                        </Grid2>

                        <Box
                            sx={{
                                backgroundColor: "#f5f5f5",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            {orderNotifications.map((notification) => (
                                <Box
                                    key={notification.id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 2,
                                        borderBottom: "1px solid #e0e0e0",
                                        pb: 2,
                                        "&:last-child": {
                                            borderBottom: "none",
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            wordBreak: "break-word",
                                            flex: 1,
                                        }}
                                    >
                                        {t(notification.message, {
                                            orderNro: notification.transportOrderNumber,
                                            vehicleName: notification.vehicleRegisterNumber,
                                            state: t(transportOrderStateNames[notification?.orderState]),
                                        })}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{
                                            whiteSpace: "nowrap",
                                            marginLeft: "16px",
                                        }}
                                    >
                                        {Base.dateToDayDateTimeStr(notification.createdDateTime).replace(/^\w+\s/, "")}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))
            ) : (
                <Box>
                    <Grid2 container gap={2} display="flex" justifyContent="center">
                        <Typography variant="body2" mt={2}>
                            {t("notifications.noNotifications")}
                        </Typography>
                    </Grid2>
                </Box>
            )}
        </MainLayout>
    );
};
